import {
  AnimationState,
  type AnimationStateValue,
  Direction,
  type DirectionValue,
  deltaToDirection,
  PawnState,
  TaskType,
} from '@dreamgame/shared';
import { AnimatedSprite, Container, Rectangle, Texture } from 'pixi.js';
import type { PawnData } from '../state/entities';
import { loadTexture, onCategoryReload } from './TextureManager';

// Animation configuration
const ANIMATION_CONFIG = {
  walk: {
    frameCount: 4,
    animationSpeed: 0.15, // frames per tick (60fps base)
    loop: true,
  },
  idle: {
    frameCount: 1,
    animationSpeed: 0.1,
    loop: true,
  },
  work: {
    frameCount: 4,
    animationSpeed: 0.12,
    loop: true,
  },
  attack: {
    frameCount: 4,
    animationSpeed: 0.2,
    loop: true,
  },
} as const;

// Target render size (smaller than tile for visual breathing room)
const RENDER_SIZE = 24;

// Offset to center pawn within tile
const PAWN_OFFSET = (32 - RENDER_SIZE) / 2;

// Direction to file prefix mapping
const DIRECTION_FILE_PREFIX: Record<DirectionValue, string> = {
  [Direction.DOWN]: 'front',
  [Direction.LEFT]: 'left',
  [Direction.RIGHT]: 'right',
  [Direction.UP]: 'back',
};

// PERFORMANCE: Mutable interface - fields updated in-place to avoid spread allocation
interface PawnAnimationState {
  direction: DirectionValue;
  animState: AnimationStateValue;
  lastX: number;
  lastY: number;
}

// Sprite data for a pawn using AnimatedSprite
interface PawnSpriteData {
  readonly container: Container;
  readonly animatedSprites: Map<string, AnimatedSprite>; // key: "direction_animState"
  readonly state: PawnAnimationState;
  activeSprite: AnimatedSprite | null;
}

// Loaded textures organized by direction
// We use walk frame 0 for idle to maintain facing direction
interface DirectionTextures {
  readonly walk: readonly Texture[];
}

// Module state
const pawnSprites: Map<number, PawnSpriteData> = new Map();
const directionTextures: Map<DirectionValue, DirectionTextures> = new Map();

// Texture loading state
let texturesLoaded = false;

// Extract frames from a horizontal strip texture
const extractFrames = ({
  texture,
  frameCount,
}: {
  texture: Texture;
  frameCount: number;
}): readonly Texture[] => {
  const frameWidth = texture.width / frameCount;
  const frameHeight = texture.height;

  const frames: Texture[] = [];
  for (let i = 0; i < frameCount; i++) {
    const frame = new Texture({
      source: texture.source,
      frame: new Rectangle(i * frameWidth, 0, frameWidth, frameHeight),
    });
    frames.push(frame);
  }

  return frames;
};

// Load textures for a single direction
const loadDirectionTextures = async ({
  direction,
}: {
  direction: DirectionValue;
}): Promise<DirectionTextures | null> => {
  const prefix = DIRECTION_FILE_PREFIX[direction];

  try {
    const walkTexture = await loadTexture({ path: `/pawns/${prefix}_walk.png` });
    const walkFrames = extractFrames({
      texture: walkTexture,
      frameCount: ANIMATION_CONFIG.walk.frameCount,
    });

    return {
      walk: walkFrames,
    };
  } catch (error) {
    console.warn(`Failed to load ${prefix} textures:`, error);
    return null;
  }
};

// Load all pawn textures
export const loadPawnTextures = async (): Promise<void> => {
  const directions: readonly DirectionValue[] = [
    Direction.DOWN,
    Direction.LEFT,
    Direction.RIGHT,
    Direction.UP,
  ];

  const results = await Promise.all(
    directions.map(async (direction) => {
      const textures = await loadDirectionTextures({ direction });
      return { direction, textures };
    })
  );

  let loadedCount = 0;
  for (const { direction, textures } of results) {
    if (textures) {
      directionTextures.set(direction, textures);
      loadedCount++;
    }
  }

  texturesLoaded = loadedCount > 0;
  console.log(`Loaded pawn textures for ${loadedCount}/${directions.length} directions`);
};

// Check if textures are loaded
export const arePawnTexturesLoaded = (): boolean => texturesLoaded;

// Reload textures (clears sprites and reloads from TextureManager)
export const reloadPawnTextures = async (): Promise<void> => {
  // Clear all existing sprites
  for (const data of pawnSprites.values()) {
    for (const sprite of data.animatedSprites.values()) {
      sprite.stop();
    }
    data.container.destroy({ children: true });
  }
  pawnSprites.clear();

  // Clear texture cache
  directionTextures.clear();
  texturesLoaded = false;

  // Reload textures
  await loadPawnTextures();
  console.log('Pawn textures reloaded');
};

// Register for texture reload events
onCategoryReload({ category: 'pawns', callback: reloadPawnTextures });

// Velocity threshold for determining if pawn is actually moving
const VELOCITY_THRESHOLD = 0.001;

const shouldFaceTaskTarget = ({ pawn }: { pawn: PawnData }): boolean => {
  const taskType = pawn.task?.taskType;
  if (taskType !== TaskType.HARVEST && taskType !== TaskType.FISH) {
    return false;
  }
  return pawn.state === PawnState.WORKING;
};

const getTaskTargetDirection = ({ pawn }: { pawn: PawnData }): DirectionValue | null => {
  if (!pawn.task || !shouldFaceTaskTarget({ pawn })) {
    return null;
  }

  const dx = pawn.task.targetX + 0.5 - (pawn.x + 0.5);
  const dy = pawn.task.targetY + 0.5 - (pawn.y + 0.5);
  if (Math.abs(dx) <= 0.01 && Math.abs(dy) <= 0.01) {
    return null;
  }

  return deltaToDirection({ dx, dy });
};

// Map PawnState to AnimationState, considering actual movement
const pawnStateToAnimState = ({
  pawnState,
  vx,
  vy,
}: {
  pawnState: number;
  vx: number;
  vy: number;
}): AnimationStateValue => {
  // Check if pawn is actually moving (velocity above threshold)
  const isActuallyMoving = Math.abs(vx) > VELOCITY_THRESHOLD || Math.abs(vy) > VELOCITY_THRESHOLD;

  switch (pawnState) {
    case PawnState.WALKING:
      // Only show walk animation if actually moving
      return isActuallyMoving ? AnimationState.WALK : AnimationState.IDLE;
    case PawnState.WORKING:
      // No dedicated work animation yet - show idle when standing still
      return isActuallyMoving ? AnimationState.WALK : AnimationState.IDLE;
    case PawnState.FIGHTING:
      return AnimationState.ATTACK;
    default:
      return AnimationState.IDLE;
  }
};

// Get sprite key for caching
const getSpriteKey = ({
  direction,
  animState,
}: {
  direction: DirectionValue;
  animState: AnimationStateValue;
}): string => {
  return `${direction}_${animState}`;
};

// Get textures for animation state
// All states use walk textures - idle uses frame 0 to maintain facing direction
const getAnimationTextures = ({
  direction,
}: {
  direction: DirectionValue;
}): readonly Texture[] | null => {
  const textures = directionTextures.get(direction);
  if (!textures) {
    return null;
  }
  return textures.walk;
};

// Get animation speed for state
const getAnimationSpeed = ({ animState }: { animState: AnimationStateValue }): number => {
  switch (animState) {
    case AnimationState.WALK:
      return ANIMATION_CONFIG.walk.animationSpeed;
    case AnimationState.WORK:
      return ANIMATION_CONFIG.work.animationSpeed;
    case AnimationState.ATTACK:
      return ANIMATION_CONFIG.attack.animationSpeed;
    default:
      return ANIMATION_CONFIG.idle.animationSpeed;
  }
};

// Create AnimatedSprite for a specific direction and state
const createAnimatedSprite = ({
  direction,
  animState,
}: {
  direction: DirectionValue;
  animState: AnimationStateValue;
}): AnimatedSprite | null => {
  const textures = getAnimationTextures({ direction });
  if (!textures || textures.length === 0) {
    return null;
  }

  const sprite = new AnimatedSprite([...textures]);
  sprite.width = RENDER_SIZE;
  sprite.height = RENDER_SIZE;
  sprite.x = PAWN_OFFSET;
  sprite.y = PAWN_OFFSET;
  sprite.animationSpeed = getAnimationSpeed({ animState });
  sprite.loop = true;
  sprite.visible = false; // Hidden by default, shown when active

  return sprite;
};

// Calculate direction from pawn velocity or position delta
const calculateDirection = ({
  pawn,
  lastX,
  lastY,
  existingDirection,
}: {
  pawn: PawnData;
  lastX: number;
  lastY: number;
  existingDirection: DirectionValue;
}): DirectionValue => {
  const taskDirection = getTaskTargetDirection({ pawn });
  if (taskDirection !== null) {
    return taskDirection;
  }

  // Prefer server-sent velocity for direction (more accurate)
  if (Math.abs(pawn.vx) > VELOCITY_THRESHOLD || Math.abs(pawn.vy) > VELOCITY_THRESHOLD) {
    return deltaToDirection({ dx: pawn.vx, dy: pawn.vy });
  }

  // Fallback to position delta
  const dx = pawn.x - lastX;
  const dy = pawn.y - lastY;
  if (Math.abs(dx) > 0.01 || Math.abs(dy) > 0.01) {
    return deltaToDirection({ dx, dy });
  }

  // Keep previous direction when standing still
  return existingDirection;
};

// Switch to a new animation when direction or state changes
const switchAnimation = ({
  existing,
  direction,
  animState,
}: {
  existing: PawnSpriteData;
  direction: DirectionValue;
  animState: AnimationStateValue;
}): void => {
  // Hide current sprite
  if (existing.activeSprite) {
    existing.activeSprite.stop();
    existing.activeSprite.visible = false;
  }

  // Get or create new sprite for this direction/state
  const spriteKey = getSpriteKey({ direction, animState });
  let newSprite = existing.animatedSprites.get(spriteKey);

  if (!newSprite) {
    newSprite = createAnimatedSprite({ direction, animState }) ?? undefined;
    if (newSprite) {
      existing.container.addChild(newSprite);
      existing.animatedSprites.set(spriteKey, newSprite);
    }
  }

  if (newSprite) {
    newSprite.visible = true;
    // For idle state, show frame 0 and stop (maintains facing direction)
    // For other states, play the animation
    if (animState === AnimationState.IDLE) {
      newSprite.gotoAndStop(0);
    } else {
      newSprite.gotoAndPlay(0);
    }
    existing.activeSprite = newSprite;
  }
};

// Update existing pawn sprite
// PERFORMANCE: Mutates state in-place instead of spread allocation
const updateExistingPawnSprite = ({
  existing,
  pawn,
  direction,
  animState,
}: {
  existing: PawnSpriteData;
  pawn: PawnData;
  direction: DirectionValue;
  animState: AnimationStateValue;
}): PawnSpriteData => {
  const stateChanged = existing.state.animState !== animState;
  const directionChanged = existing.state.direction !== direction;

  if (stateChanged || directionChanged) {
    switchAnimation({ existing, direction, animState });
  }

  existing.state.direction = direction;
  existing.state.animState = animState;
  existing.state.lastX = pawn.x;
  existing.state.lastY = pawn.y;

  return existing;
};

// Create new pawn sprite data
const createNewPawnSprite = ({
  pawn,
  parentContainer,
  direction,
  animState,
}: {
  pawn: PawnData;
  parentContainer: Container;
  direction: DirectionValue;
  animState: AnimationStateValue;
}): PawnSpriteData | null => {
  const container = new Container();
  const animatedSprites = new Map<string, AnimatedSprite>();
  const spriteKey = getSpriteKey({ direction, animState });
  const initialSprite = createAnimatedSprite({ direction, animState });

  if (!initialSprite) {
    return null;
  }

  initialSprite.visible = true;
  // For idle state, show frame 0 and stop (maintains facing direction)
  // For other states, play the animation
  if (animState === AnimationState.IDLE) {
    initialSprite.gotoAndStop(0);
  } else {
    initialSprite.play();
  }
  container.addChild(initialSprite);
  animatedSprites.set(spriteKey, initialSprite);
  parentContainer.addChild(container);

  const newData: PawnSpriteData = {
    container,
    animatedSprites,
    state: { direction, animState, lastX: pawn.x, lastY: pawn.y },
    activeSprite: initialSprite,
  };

  pawnSprites.set(pawn.networkId, newData);
  return newData;
};

// Create or update sprite for pawn
export const updatePawnSprite = ({
  pawn,
  parentContainer,
  delta: _delta,
}: {
  pawn: PawnData;
  parentContainer: Container;
  delta: number;
}): PawnSpriteData | null => {
  if (!texturesLoaded) {
    return null;
  }

  const existing = pawnSprites.get(pawn.networkId);
  const animState = pawnStateToAnimState({
    pawnState: pawn.state,
    vx: pawn.vx,
    vy: pawn.vy,
  });
  const lastX = existing?.state.lastX ?? pawn.x;
  const lastY = existing?.state.lastY ?? pawn.y;
  const existingDirection = existing?.state.direction ?? Direction.DOWN;
  const direction = calculateDirection({ pawn, lastX, lastY, existingDirection });

  if (existing) {
    return updateExistingPawnSprite({ existing, pawn, direction, animState });
  }

  return createNewPawnSprite({ pawn, parentContainer, direction, animState });
};

// Get sprite data for a pawn
export const getPawnSprite = ({ networkId }: { networkId: number }): PawnSpriteData | undefined => {
  return pawnSprites.get(networkId);
};

// Remove sprite for pawn
export const removePawnSprite = ({ networkId }: { networkId: number }): void => {
  const data = pawnSprites.get(networkId);
  if (data) {
    // Stop all animations
    for (const sprite of data.animatedSprites.values()) {
      sprite.stop();
    }
    data.container.destroy({ children: true });
    pawnSprites.delete(networkId);
  }
};

// Clean up sprites for pawns that no longer exist
export const cleanupRemovedPawns = ({
  activeNetworkIds,
}: {
  activeNetworkIds: Set<number>;
}): void => {
  for (const networkId of pawnSprites.keys()) {
    if (!activeNetworkIds.has(networkId)) {
      removePawnSprite({ networkId });
    }
  }
};

// Get direction for a pawn (for external use like rendering highlights)
export const getPawnDirection = ({ networkId }: { networkId: number }): DirectionValue => {
  const data = pawnSprites.get(networkId);
  return data?.state.direction ?? Direction.DOWN;
};
